import axios from 'axios';
import { ApiResponse, zps_ea_war } from '../zps';

export type IAPMTransaction = {
  key: APMTransKey;
  gid: number;
  entid: number;
  sid: number;
  amount: number;
  bankid: number;
  cid: number;
  dateTime: string;
};

export type IAPMPeriods = {
  key: APMTransKey;
  gid: number;
  entid: number;
  sid: number;
  amount: number;
  amountCommitions: number;
  amountWithoutCommitions: number;
  bankid: number;
  cid: number;
  dateTime: string;
  totalTransactions: number;
};

export type IAPMPComissions = {
  key: APMTransKey;
  a052: number;
  a054: string;
  amount: number;
  amountCommission: number;
  cid: number;
  pCommission: string;
};

export type APMTransKey = {
  a001: number;
  a029: number;
  a031: number;
  a032: number;
  dateTime: string;
  movType: string;
};

export interface IAPMTransactionResponse extends ApiResponse {
  payment: boolean;
  totalRows: number;
  transactions: IAPMTransaction[];
  type: string;
}

export interface IAPMPeriodsResponse extends ApiResponse {
  payment: boolean;
  totalRows: number;
  periods: IAPMPeriods[];
  type: string;
  commissions: IAPMPComissions[];
}

export type IAPMPos = {
  description: string;
  entid: number;
  posid: number;
  sid: number;
};

export interface fetchApmPOSEquipmentListResponse extends ApiResponse {
  poss: IAPMPos[];
}

export type apmEquipPos = {
  account: number;
  active: boolean;
  bankid: number;
  description: string;
  eid?: number;
  entid: number;
  gid: number;
  inuse: number;
  mbway: boolean;
  merchantid: number;
  posid: number;
  poskey?: string;
  sid: number;
  timeopenclose?: number;
  zsversion: number;
};

export type IAPMPosEquipment = {
  eid?: number;
  lastComDt?: string;
  lastOperation?: string;
  lastOperationDt?: string;
  status?: number;
  pos: apmEquipPos;
};

export interface fetchPOSEquipmentDetailsResponse extends ApiResponse {
  poss: IAPMPosEquipment[];
  totalRows: number;
}

export interface ReceiptResponse extends ApiResponse {
  receipt: string;
}

export const fetchAPMTransactionList = (
  controller: AbortController,
  entid: number,
  sid: number,
  posid: number,
  offset: number,
  limit: number,
  totalRows: boolean,
  begin?: string,
  end?: string
): Promise<IAPMTransactionResponse> => {
  const url =
    `${zps_ea_war}/apm/transaction?entid=${entid}&sid=${sid}&posid=${posid}&offset=${offset}&limit=${limit}&totalRows=${totalRows}` +
    (begin ? `&begin=${begin}` : '') +
    (end ? `&end=${end}` : '');

  return axios.get<IAPMTransactionResponse>(url, { signal: controller.signal }).then(res => res.data);
};

export const fetchApmPOSEquipmentList = (
  controller: AbortController,
  entid: number,
  sid: number
): Promise<fetchApmPOSEquipmentListResponse> => {
  return axios
    .get<fetchApmPOSEquipmentListResponse>(`${zps_ea_war}/apm/pos/label?entid=${entid}&sid=${sid}`, {
      signal: controller.signal
    })
    .then(res => res.data);
};

export const fetchAPMPeriodsList = (
  controller: AbortController,
  entid: number,
  sid: number,
  posid: number,
  offset: number,
  limit: number,
  totalRows: boolean,
  begin?: string,
  end?: string
): Promise<IAPMPeriodsResponse> => {
  const url =
    `${zps_ea_war}/apm/period?entid=${entid}&sid=${sid}&posid=${posid}&offset=${offset}&limit=${limit}&totalRows=${totalRows}` +
    (begin ? `&begin=${begin}` : '') +
    (end ? `&end=${end}` : '');

  return axios.get<IAPMPeriodsResponse>(url, { signal: controller.signal }).then(res => res.data);
};

export const fetchReceiptData = (
  a001: number,
  movType: string,
  a031: number,
  a032: number,
  a029: number,
  dateTime: string,
  controller: AbortController
): Promise<ReceiptResponse> => {
  const url = `${zps_ea_war}/apm/receipt?a001=${a001}&movType=${movType}&a031=${a031}&a032=${a032}&a029=${a029}&dateTime=${new Date(
    dateTime
  ).toISOString()}`;

  return axios.get<ReceiptResponse>(url, { signal: controller.signal }).then(res => res.data);
};

export const fetchComissionsData = (
  a001: number,
  movType: string,
  a031: number,
  a032: number,
  a029: number,
  dateTime: string,
  controller: AbortController
): Promise<IAPMPeriodsResponse> => {
  const url = `${zps_ea_war}/apm/period/commission?a001=${a001}&movType=${movType}&a031=${a031}&a032=${a032}&a029=${a029}&dateTime=${new Date(
    dateTime
  ).toISOString()}`;

  return axios.get<IAPMPeriodsResponse>(url, { signal: controller.signal }).then(res => res.data);
};

export const fetchTransactionData = (
  a001: number,
  movType: string,
  a031: number,
  a032: number,
  a029: number,
  dateTime: string,
  controller: AbortController
): Promise<IAPMTransactionResponse> => {
  const url = `${zps_ea_war}/apm/period/transaction?a001=${a001}&movType=${movType}&a031=${a031}&a032=${a032}&a029=${a029}&dateTime=${new Date(
    dateTime
  ).toISOString()}`;

  return axios.get<IAPMTransactionResponse>(url, { signal: controller.signal }).then(res => res.data);
};

export const fetchPOSEquipmentDetails = (
  controller: AbortController,
  entid: number,
  sid: number,
  posid: number,
  offset: number,
  limit: number,
  totalRows: boolean
): Promise<fetchPOSEquipmentDetailsResponse> => {
  const url = `${zps_ea_war}/apm/pos/details?entid=${entid}&sid=${sid}&offset=${offset}&posid=${posid}&limit=${limit}&totalRows=${totalRows}`;

  return axios.get<fetchPOSEquipmentDetailsResponse>(url, { signal: controller.signal }).then(res => res.data);
};

export const updateApmEquipmentDetails = (
  posid: number,
  timeopenclose: number,
  description: string,
  controller: AbortController
): Promise<ApiResponse> => {
  const url = `${zps_ea_war}/apm/pos/details/`;

  return axios
    .put<ApiResponse>(url, { posid, timeopenclose, description }, { signal: controller.signal })
    .then(res => res.data);
};
